import React, { useEffect, useRef, useState } from "react"
import { CheckoutOrder } from "../../../models/order"
import { AdyenCheckout, Swish } from "@adyen/adyen-web"
import {
  getAdyenCountryCode,
  getAdyenEnvironment,
  getAdyenLocale,
} from "../../../util/util"
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import {
  deleteAdyenSwishAction,
  getAdyenSwishAction,
} from "../../../services/adyen-swish-service"
import { CardFormTemplate } from "./CardFormTemplate"
import { usePaymentService } from "../../../hooks/use-payment-service"
import SSESubscriberService from "../../../services/SSESubscriberService"
import { getErrorMessage } from "../../../util/error-handler"
import { routes } from "../../../routes"
import { translationKeys } from "../../../util/translationKeys"
import { captureException } from "../../../services/exception-logger"
import { trackPaymentStage } from "../../../services/payment-telemetry"

type Props = {
  showPaymentFailedScreen: (
    errorMessage?: string,
    providerName?: string
  ) => void
  showPaymentSucceededScreen: () => void
  order: CheckoutOrder
  countryCode: string
  paymentProcessing: boolean
  setPaymentProcessing: React.Dispatch<React.SetStateAction<boolean>>
  sseSubscriber: React.MutableRefObject<SSESubscriberService | undefined>
  slug: string
}

export const AdyenSwishWalletQRScanner = ({
  showPaymentFailedScreen,
  showPaymentSucceededScreen,
  order,
  countryCode,
  slug,
  paymentProcessing,
  sseSubscriber,
}: Props) => {
  const [isBackClickedLoading, setIsBackClickedLoading] = useState(false)
  const SwishRef = useRef<HTMLDivElement>(null)
  const swishComponentRef = useRef<Swish | null>(null)

  const { language } = useI18next()
  const { handleCancelPayment } = usePaymentService()
  const { navigate } = useI18next()

  const { t } = useTranslation()

  const onBackClicked = async () => {
    if (paymentProcessing) {
      return
    }
    setIsBackClickedLoading(true)
    sseSubscriber?.current?.close()
    // Sometimes there is a race condition between close() and calling a function that triggers a message event from backend
    // This setTimeout is used to make sure that the connection is closed, and that cancel call is not triggering an event
    // We want to avoid sending users to PaymentFailedScreen momentarily
    setTimeout(async () => {
      try {
        await handleCancelPayment(order.order.id, showPaymentSucceededScreen)
        deleteAdyenSwishAction()
        navigate(
          routes.checkout.base({ countryCode, slug, orderId: order.order.id })
        )
      } catch (error: any) {
        const errorMessage = getErrorMessage(error, t)
        captureException(error, {
          type: "network",
          target: "internal-api",
          category: "payment",
          step: "cancel-payment",
          provider: "adyen",
          context: {
            errorSource: "AdyenSwishWalletQRScanner - onBackClicked",
            errorMessage,
            shop: slug,
            orderId: order.order.id,
          },
        })
        showPaymentFailedScreen(errorMessage, "Swish wallet")
      }
      setIsBackClickedLoading(false)
    }, 1000)
  }

  useEffect(() => {
    const mountSwish = !!SwishRef.current && !swishComponentRef.current

    if (!mountSwish) {
      return
    }

    const init = async () => {
      const storedAction = getAdyenSwishAction()

      if (!storedAction) {
        captureException(
          new Error(
            "Swish payment attempted, but no action found in localStorage"
          ),
          {
            type: "business_logic",
            category: "invalid-state",
            context: {
              errorSource: "AdyenSwishWalletQRScanner",
              shop: slug,
              orderId: order.order.id,
            },
          }
        )
        showPaymentFailedScreen(
          "Something went wrong with Swish payment",
          "Swish"
        )
        return
      }

      try {
        const checkout = await AdyenCheckout({
          clientKey: storedAction.clientKey,
          environment: getAdyenEnvironment(storedAction.clientKey),
          locale: getAdyenLocale(language),
          countryCode: getAdyenCountryCode(countryCode),
          onError: (error) => {
            captureException(error, {
              type: "network",
              target: "external-api",
              category: "payment",
              step: "provider-action",
              provider: "adyen",
              context: {
                errorSource: "AdyenSwishWalletQRScanner - onError",
                shop: slug,
                orderId: order.order.id,
              },
            })
            showPaymentFailedScreen(error.message, "Swish")
          },
        })

        checkout.createFromAction(storedAction.action).mount(SwishRef.current!)
        trackPaymentStage("adyen", "swish-qr-shown", {
          shop: slug,
          orderId: order.order.id,
        })
      } catch (error) {
        captureException(error, {
          type: "network",
          target: "external-api",
          category: "payment",
          step: "provider-action",
          provider: "adyen",
          context: {
            errorSource: "AdyenSwishWalletQRScanner - mount",
            shop: slug,
            orderId: order.order.id,
          },
        })
        showPaymentFailedScreen(
          "Something went wrong with Swish payment",
          "Swish"
        )
      }
    }

    init()
  }, [])

  return (
    <CardFormTemplate
      onBackClicked={onBackClicked}
      cancelPaymentLoading={isBackClickedLoading}
      formSubmissionLoading={false}
      title={t(translationKeys.commonSwishPaymentTitle)}
      subtitle={t(translationKeys.commonSwishPaymentDescription)}
    >
      <div ref={SwishRef} />
    </CardFormTemplate>
  )
}
